// ── Auth context + API client ──
import { createContext, useContext } from "react";

export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// Surface the server's actual error instead of just the status code.
//
// Read as TEXT, not json(). A failure whose body is not JSON — a gateway error
// page, a truncated response from a request the platform cut off mid-flight, an
// empty body — is exactly the failure you most need to see, and res.json()
// throws it away. "failed: 500" tells you only the thing you already knew.
//
// The distinction matters: an error our own code raised arrives as JSON with an
// `error` field, so anything else means the request died somewhere between the
// browser and the function and no handler of ours ever ran.
const errMsg = async (res, prefix) => {
  let text = "";
  try { text = await res.text(); } catch (_) { /* body already consumed or gone */ }

  if (text) {
    try {
      const body = JSON.parse(text);
      if (body && body.error) return body.error;
      // JSON, but not our error shape. Show it rather than discarding it.
      return prefix + " failed: " + res.status + " — " + text.slice(0, 300);
    } catch (_) {
      // Not JSON at all: the platform answered, not us. Say so, because it
      // changes where to look — infrastructure, not the function.
      const clean = text.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
      return prefix + " failed: " + res.status
           + " — the response did not come from the application"
           + (clean ? ": " + clean.slice(0, 200) : " and carried no detail.");
    }
  }
  return prefix + " failed: " + res.status + " — empty response, so the request was almost certainly "
       + "cut off before the server finished (a long-running parse or import will do this).";
};

// onUnauthorized (optional) fires whenever the server answers 401 — i.e. the
// auth cookie/token died while the SPA was still showing a signed-in shell.
// App wires this to the session-expired handler so the user is bounced to a
// fresh sign-in instead of being left on an app full of empty pages. It runs
// BEFORE the throw, so the many `catch(e){}` blocks scattered through the
// pages can't swallow the signal.
// `sentToken` matters: a loader holding a stale closure can fire a request with
// NO credentials attached and get the same 401 a dead session gives. The
// handler needs to tell those apart before it invalidates anyone.
const unauthorized = (onUnauthorized, info) => {
  try { if (onUnauthorized) onUnauthorized(info); } catch (_) { /* never mask the throw */ }
  return new Error("UNAUTHORIZED");
};

// getImpersonateId (optional) returns the id of the user an admin is "testing
// as", or null. It goes out as x-impersonate-user and the SERVER decides
// whether to honour it — it is only accepted when the authenticated caller is
// genuinely an admin, and every write made under it is audited with both
// identities. Before this the header did not exist and impersonation was
// cosmetic: the SPA re-rendered as the other user while the API kept answering
// as the admin, so anything enforced server-side looked wide open when tested.
// ── THE TOKEN DOES NOT TRAVEL IN `Authorization` ─────────────────────────────
//
// Azure Static Web Apps rewrites that header on its way to the linked API. Our
// signed token goes out of the browser and a DIFFERENT one arrives at the
// function: no id, no email, no role, a short expiry, and a signature we did
// not produce. Every request then 401s with a perfectly valid session behind it
// — which on the deployed site is the sign-in loop, and never reproduces on
// localhost because nothing sits in front of the API there.
//
// So the token rides in a header the platform has no opinion about. Authorization
// is still sent alongside it: local development has no SWA layer, nothing
// rewrites it there, and any caller or tool that expects the standard header
// keeps working. The server reads x-ace-token first and falls back.
const TOKEN_HEADER = "x-ace-token";

// ── THE UPLOAD CEILING IS THE PLATFORM'S, NOT OURS ───────────────────────────
//
// Azure Static Web Apps rejects any request body over 30 MB at its gateway,
// with a bare 413 and no JSON body, BEFORE the function is invoked. It is not
// configurable — FUNCTIONS_REQUEST_BODY_SIZE_LIMIT does not move it.
//
// This is why an upload that parses perfectly against local `func` fails on the
// deployed site: nothing sits in front of the API on localhost. Same class of
// problem as the Authorization header above, and the same tell — works locally,
// fails deployed, and the error names a status instead of a cause.
//
// WE SEND FILES AS BASE64 INSIDE JSON, which costs 4 bytes for every 3. So the
// real limit on the FILE is three quarters of the limit on the body:
//
//     21 MB file -> 28.0 MB encoded      fits
//     22 MB file -> 29.3 MB encoded      fits, barely
//     25 MB file -> 33.3 MB encoded      413
//
// 21 MB is the budget below. The remaining headroom covers the rest of the JSON
// — field names, the Box URL, the base64 padding — and the gateway's own idea
// of how big the body is, which we cannot see. A threshold set right at the
// cliff edge is one that fails for a single borrower and nobody knows why.
export const SWA_MAX_BODY_BYTES = 30 * 1024 * 1024;
export const MAX_UPLOAD_FILE_BYTES = 21 * 1024 * 1024;

const mbStr = n => (n / 1024 / 1024).toFixed(1) + " MB";

// Returns an explanatory message, or null when the file will fit.
//
// Call this BEFORE reading the file. Encoding a 24 MB workbook costs real
// seconds and pushing it at the gateway costs several more, and every one of
// them is spent on a request that was always going to be refused.
//
// The message names the file's own size AND what it becomes, because "too
// large" alone leaves you guessing which of the two numbers you have to get
// under.
export const checkUploadSize = (file) => {
  if (!file || typeof file.size !== "number") return null;
  if (file.size <= MAX_UPLOAD_FILE_BYTES) return null;
  const encoded = Math.ceil(file.size / 3) * 4;
  return `${file.name || "That file"} is ${mbStr(file.size)}, which becomes ${mbStr(encoded)} once encoded for `
       + `upload — past the ${mbStr(SWA_MAX_BODY_BYTES)} ceiling the hosting platform puts on one request. `
       + `Files up to ${mbStr(MAX_UPLOAD_FILE_BYTES)} go through. Nothing was sent.`;
};

// ── AN EMPTY 2xx BODY IS A VALID ANSWER, NOT A PARSE ERROR ──────────────────
//
// res.json() throws "Unexpected end of JSON input" on an empty body, and an
// empty body is a thing our own API returns routinely: an Azure Function that
// sets `body: null` sends no bytes at all. getIntegrations does exactly that for
// a provider with no row yet — which is the NORMAL state of an integration
// before its migration has been run.
//
// The result was a settings page that could not tell "this integration is not
// configured yet" from "something is broken", and reported the second. So an
// empty body now reads as null, which is what the server meant by it, and every
// caller that already writes `d || {}` keeps working.
//
// 204 No Content is handled by the same path rather than a special case — it is
// the same fact stated in the status line.
const parseJson = async (res) => {
  if (res.status === 204) return null;
  const text = await res.text();
  if (!text) return null;
  try { return JSON.parse(text); }
  catch (e) {
    // A non-empty body that is not JSON is a real problem, and saying so beats
    // the generic parser message — it usually means something between the
    // browser and the function answered instead of the function.
    throw new Error("The server's reply was not JSON: " + text.slice(0, 200));
  }
};

export const makeApi = (getToken, onUnauthorized, getImpersonateId) => {
  const authHeaders = (token) => {
    const h = token
      ? { [TOKEN_HEADER]: token, "Authorization": "Bearer " + token }
      : {};
    const imp = getImpersonateId && getImpersonateId();
    if (imp) h["x-impersonate-user"] = String(imp);
    return h;
  };

  return {
  get: async (path, params) => {
    const token = getToken();
    const headers = authHeaders(token);
    // Build the URL, then append a UNIQUE cache-buster (_=<timestamp>). no-store stops the
    // browser cache; the unique param additionally defeats any edge/proxy/CDN cache that
    // ignores Cache-Control and keys only on the URL — that's what makes a saved edit still
    // read stale until a full refresh. Together they guarantee every GET is fresh.
    let url = "/api/" + path;
    if (params) url += (url.includes("?") ? "&" : "?") + new URLSearchParams(params).toString();
    url += (url.includes("?") ? "&" : "?") + "_=" + Date.now();
    const res = await fetch(url, { headers, cache: "no-store" });
    if (res.status === 401) throw unauthorized(onUnauthorized, { path, sentToken: !!token });
    if (!res.ok) throw new Error(await errMsg(res, "GET " + path));
    return parseJson(res);
  },
  post: async (path, body) => {
    const token = getToken();
    const headers = { "Content-Type": "application/json", ...authHeaders(token) };
    const payload = JSON.stringify(body);
    const res = await fetch("/api/" + path, { method:"POST", headers, body:payload });
    if (res.status === 401) throw unauthorized(onUnauthorized, { path, sentToken: !!token });
    // 413 never comes from our own code — no function in this API returns it.
    // It is the gateway refusing the body, so errMsg would read the empty
    // response and produce "POST x failed: 413", which says nothing about size
    // and sends you looking at the function that never ran. Name the real cause
    // and the real number instead.
    if (res.status === 413) {
      throw new Error(
        `That request was ${mbStr(payload.length)}, over the ${mbStr(SWA_MAX_BODY_BYTES)} limit the hosting `
        + `platform enforces per request — it was refused before reaching the server, so nothing was saved. `
        + `If this was a file upload, the file needs to be under ${mbStr(MAX_UPLOAD_FILE_BYTES)}.`);
    }
    if (!res.ok) throw new Error(await errMsg(res, "POST " + path));
    return parseJson(res);
  }
  };
};
